/**
 * The time-window rule the two coach create forms share.
 *
 * One definition, used by both `NewCoachSeriesPage` (recurring) and
 * `NewOneTimeCoachSessionPage` (single session), so the client cannot end up
 * with two subtly different ideas of a legal window.
 *
 * What is deliberately NOT here: the minimum duration. prospin-api reads that
 * from `CLUB_MIN_SLOT_MINUTES` (an env var, `app/config.py`), so a mirrored
 * client constant would be wrong on any deployment that retunes it. The API
 * owns the threshold and its 400 text is what staff read — same stance as
 * `coach-series.ts:durationMinutes`. Slot *alignment* isn't checked here
 * either: `SlotWindowFields` only offers aligned options, so an unaligned time
 * is unreachable through the UI.
 */
import { minutesOfDay } from './time';

/**
 * The problem with a start/end pair, or null when there is nothing to say.
 *
 * An incomplete window returns null rather than an error: the caller's submit
 * button is already disabled, and shouting "end must be after start" at
 * someone who has not picked an end yet is noise.
 */
export function slotWindowError(startTime: string, endTime: string): string | null {
  if (!startTime || !endTime) return null;
  const start = minutesOfDay(startTime);
  const end = minutesOfDay(endTime);
  if (start === null || end === null) return 'Enter a valid time window.';
  // Strictly greater, on minute-of-day: this is also what keeps a window from
  // wrapping past midnight (23:00 → 01:00 reads as 1380 → 60). The 24:00
  // end-of-day sentinel is 1440 and so is later than every start time.
  if (end <= start) return 'End time must be after start time.';
  return null;
}
